'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  OPEN_DAY_START,
  isOpenDayLive,
  isOpenDayOver,
  openDayStartMs,
} from '@/config/openDay';

export interface OpenDayCountdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/** `?openDay=upcoming|live|over` forces a phase so all three states are reachable in dev and QA. */
const FORCED_PHASES = ['upcoming', 'live', 'over'] as const;

type ForcedPhase = (typeof FORCED_PHASES)[number];

/**
 * Read straight from the URL rather than through nuqs.
 *
 * nuqs calls Next's useSearchParams, and this hook runs inside the site-wide
 * banner, so going through it would drag every statically prerendered landing
 * page into a client-side bailout. The override is a dev and QA affordance
 * that only ever runs after mount, so it does not need reactivity.
 */
const readForcedPhase = (): ForcedPhase | null => {
  try {
    const value = new URLSearchParams(window.location.search).get('openDay');

    return FORCED_PHASES.find((phase) => phase === value) ?? null;
  } catch {
    return null;
  }
};

const DISMISS_KEY = `me_open_day_dismissed_${OPEN_DAY_START}`;

const countdownFrom = (ms: number): OpenDayCountdown => {
  const remaining = Math.max(0, ms);

  return {
    days: Math.floor(remaining / 86_400_000),
    hours: Math.floor((remaining % 86_400_000) / 3_600_000),
    minutes: Math.floor((remaining % 3_600_000) / 60_000),
    seconds: Math.floor((remaining % 60_000) / 1000),
  };
};

const readDismissed = (): boolean => {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === 'true';
  } catch {
    // Private browsing or blocked storage: show the bar rather than crash.
    return false;
  }
};

/**
 * Phase, countdown and dismissal for the open day.
 *
 * `mounted` guards against hydration mismatch: the server has no clock the
 * client agrees with, so nothing time-dependent renders until after mount.
 */
export function useOpenDay() {
  const [mounted, setMounted] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState(false);
  const [phaseOverride, setPhaseOverride] = useState<ForcedPhase | null>(null);

  useEffect(() => {
    setMounted(true);
    setDismissed(readDismissed());
    setPhaseOverride(readForcedPhase());
  }, []);

  const isOver = phaseOverride ? phaseOverride === 'over' : isOpenDayOver(now);

  useEffect(() => {
    // Nothing on the page moves once the doors have closed, so the site-wide
    // banner must not keep a timer alive on every page for the rest of time.
    if (isOver) return;

    // One second, because the hero shows a live seconds column.
    const tick = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(tick);
  }, [isOver]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, 'true');
    } catch {
      // Dismissal is a convenience, not state we need to guarantee.
    }
  }, []);

  const isLive = phaseOverride ? phaseOverride === 'live' : isOpenDayLive(now);

  return {
    mounted,
    isOver,
    isLive,
    dismissed,
    dismiss,
    countdown: countdownFrom(openDayStartMs() - now),
    /** Inside a week: near enough that a countdown is information, not pressure. */
    isImminent: !isOver && openDayStartMs() - now <= 7 * 24 * 3_600_000,
  };
}
